import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import '../../App.css';
import './PromptEditPage.css';
import api from '../../utils/api.js';
import { handleApiError } from '../../utils/errorHandler.js';
import { useAuth } from '../../contexts/AuthContext.js';
import SkeletonLoader from '../../components/SkeletonLoader.js';

function PromptEditPage() {
    const { templateId } = useParams();
    const [searchParams] = useSearchParams();
    const isEditMode = !!templateId;

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [hasChanges, setHasChanges] = useState(!isEditMode); // 新規作成時は最初から変更あり扱い

    // フォーム状態
    const [formName, setFormName] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formContent, setFormContent] = useState('');

    // 初期値（変更検知用）
    const [initialData, setInitialData] = useState({ name: '', description: '', content: '' });

    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // 管理者チェック
    useEffect(() => {
        if (currentUser && !currentUser.is_admin) {
            navigate('/');
        }
    }, [currentUser, navigate]);

    // 既存テンプレートの取得（編集モード時）
    const fetchTemplate = useCallback(async () => {
        if (!templateId) return;

        setLoading(true);
        try {
            const response = await api.fetch(`/prompts/templates/${templateId}`);
            if (response && response.ok) {
                const data = await response.json();
                setFormName(data.name);
                setFormDescription(data.description || '');
                setFormContent(data.content);
                setInitialData({
                    name: data.name,
                    description: data.description || '',
                    content: data.content
                });
            } else {
                const errorMessage = await handleApiError(response, 'プロンプトが見つかりませんでした。');
                setError(errorMessage);
            }
        } catch (err) {
            setError('通信エラーが発生しました。');
        } finally {
            setLoading(false);
        }
    }, [templateId]);

    useEffect(() => {
        if (isEditMode) {
            fetchTemplate();
        }
    }, [isEditMode, fetchTemplate]);

    // 変更検知
    useEffect(() => {
        if (isEditMode) {
            const changed =
                formName !== initialData.name ||
                formDescription !== initialData.description ||
                formContent !== initialData.content;
            setHasChanges(changed);
        }
    }, [formName, formDescription, formContent, initialData, isEditMode]);

    // キャンセル処理
    const handleCancel = () => {
        if (isEditMode) {
            // 編集モード: 元の値に戻す
            setFormName(initialData.name);
            setFormDescription(initialData.description);
            setFormContent(initialData.content);
            setError(null);
        } else {
            // 新規作成: 一覧に戻る
            navigate('/settings/prompts');
        }
    };

    // 保存処理
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!formName.trim() || !formContent.trim()) {
            setError('名前と内容は必須です。');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const url = isEditMode
                ? `/prompts/templates/${templateId}`
                : '/prompts/templates';
            const method = isEditMode ? 'PUT' : 'POST';

            const body = {
                name: formName,
                description: formDescription || null,
                content: formContent
            };

            // 新規作成時はprompt_typeを追加
            if (!isEditMode) {
                body.prompt_type = searchParams.get('type') || 'summary';
            }

            const response = await api.fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (response && response.ok) {
                navigate('/settings/prompts');
            } else {
                const errorMessage = await handleApiError(
                    response,
                    isEditMode ? 'プロンプトの更新に失敗しました。' : 'プロンプトの作成に失敗しました。'
                );
                setError(errorMessage);
            }
        } catch (err) {
            setError('通信エラーが発生しました。');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="prompt-edit-page">
                <SkeletonLoader variant="text" rows={6} />
            </div>
        );
    }

    return (
        <div className="prompt-edit-page">
            {/* ヘッダー */}
            <div className="prompt-edit-header">
                <Link to="/settings/prompts" className="prompt-edit-back-link">
                    ← プロンプト一覧に戻る
                </Link>
            </div>

            <div className="prompt-edit-card">
                <h1 className="page-title">
                    {isEditMode ? 'プロンプト編集' : '新規プロンプト作成'}
                </h1>

                <form onSubmit={handleSubmit} className="prompt-edit-form">
                    <div className="prompt-form-group">
                        <label className="prompt-form-label">
                            名前<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            className="prompt-form-input"
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            placeholder="例: カスタム要約テンプレート"
                            required
                        />
                    </div>

                    <div className="prompt-form-group">
                        <label className="prompt-form-label">説明</label>
                        <input
                            type="text"
                            className="prompt-form-input"
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            placeholder="このプロンプトの用途や特徴を入力してください"
                        />
                    </div>

                    <div className="prompt-form-group">
                        <label className="prompt-form-label">
                            プロンプト内容<span className="required">*</span>
                        </label>
                        <textarea
                            className="prompt-form-input prompt-form-textarea"
                            value={formContent}
                            onChange={(e) => setFormContent(e.target.value)}
                            placeholder="要約生成に使用するプロンプトを入力してください..."
                            maxLength={15000}
                            required
                        />
                        <small style={{ color: formContent.length > 14000 ? '#ff6b6b' : undefined }}>
                            {formContent.length.toLocaleString()} / 15,000 文字
                        </small>
                    </div>
                </form>
            </div>

            {/* 下部フローティングバー（変更がある場合に表示） */}
            {hasChanges && (
                <div className="edit-actions-footer">
                    {error && <div className="modal-error-message">{error}</div>}
                    <button
                        type="button"
                        className="modal-button-cancel"
                        onClick={handleCancel}
                        disabled={saving}
                    >
                        キャンセル
                    </button>
                    <button
                        type="submit"
                        className="modal-button-confirm"
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        {saving ? '保存中...' : (isEditMode ? '保存' : '作成')}
                    </button>
                </div>
            )}
        </div>
    );
}

export default PromptEditPage;
